import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { SupportUserAvailabilityOverride } from "@redotech/crdb-enums/support-v2/support-user-availability-override";
import { WorkScheduleAvailability } from "@redotech/redo-support-common/work-schedule/types";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";
import { MongoUserId } from "../ids/mongo-ids";

export const TimeBlock = Schema.Struct({
  start: Schema.optional(Schema.String),
  end: Schema.optional(Schema.String),
});

export const DaySchedule = Schema.Struct({
  enabled: Schema.Boolean,
  timeBlocks: Schema.Array(TimeBlock),
});

export const WeeklySchedule = Schema.Struct({
  sunday: Schema.optional(DaySchedule),
  monday: Schema.optional(DaySchedule),
  tuesday: Schema.optional(DaySchedule),
  wednesday: Schema.optional(DaySchedule),
  thursday: Schema.optional(DaySchedule),
  friday: Schema.optional(DaySchedule),
  saturday: Schema.optional(DaySchedule),
});

export const ScheduleOverride = Schema.Struct({
  id: Schema.String,
  /** `YYYY-MM-DD` local date in the schedule's timezone. */
  date: Schema.String,
  available: Schema.Boolean,
  timeBlocks: Schema.Array(TimeBlock),
  note: Schema.optional(Schema.String),
});

export const AvailabilityOverrideStatus = Schema.Enums(
  SupportUserAvailabilityOverride,
);

export const WorkScheduleView = Schema.Struct({
  timezone: Schema.String,
  scheduleEnabled: Schema.Boolean,
  weeklyHours: WeeklySchedule,
  scheduleOverrides: Schema.Array(ScheduleOverride),
  /** Null means every channel. */
  assignableChannels: Schema.NullOr(Schema.Array(Schema.String)),
  overrideStatus: Schema.NullOr(AvailabilityOverrideStatus),
  overrideExpiresAt: Schema.NullOr(Schema.Date),
});

export const GetWorkScheduleInput = Schema.Struct({
  /** Omit to read your own. Requires user-management permission when set. */
  targetMongoUserId: Schema.optional(MongoUserId),
});

export const ShiftStatusView = Schema.Struct({
  active: Schema.Boolean,
  /** Optional while the API catches up; absent means derive it from `active`. */
  availability: Schema.optional(Schema.Enums(WorkScheduleAvailability)),
  reason: Schema.Literal(
    "no_schedule",
    "manual_override",
    "schedule_override",
    "within_hours",
    "outside_hours",
  ),
  nextTransitionAt: Schema.Date,
  nextTransitionActive: Schema.Boolean,
});

export const GetWorkScheduleResponse = Schema.Struct({
  schedule: WorkScheduleView,
  status: ShiftStatusView,
});

export const UpdateWorkScheduleInput = Schema.Struct({
  targetMongoUserId: Schema.optional(MongoUserId),
  timezone: Schema.String,
  scheduleEnabled: Schema.Boolean,
  weeklyHours: WeeklySchedule,
  scheduleOverrides: Schema.Array(ScheduleOverride),
});

export const GetShiftStatusesInput = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId),
});

/** `assignableChannels: null` means the agent takes every channel. */
export const UserShiftStatus = Schema.Struct({
  mongoUserId: MongoUserId,
  active: Schema.Boolean,
  assignableChannels: Schema.NullOr(Schema.Array(Schema.String)),
});

export const GetShiftStatusesResponse = Schema.Struct({
  statuses: Schema.Array(UserShiftStatus),
});

export const GetTeamWorkSchedulesInput = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId),
});

export const UserWorkSchedule = Schema.Struct({
  mongoUserId: MongoUserId,
  schedule: WorkScheduleView,
});

/**
 * `teamTimezone` is the clock a coverage view renders in: every schedule
 * carries its own zone, so only a shared one makes overlap mean anything.
 */
export const GetTeamWorkSchedulesResponse = Schema.Struct({
  teamTimezone: Schema.String,
  schedules: Schema.Array(UserWorkSchedule),
});

/**
 * `channels: null` restores every channel. `targetMongoUserId` requires
 * MANAGE_TEAM_USERS and defaults to the caller.
 */
export const UpdateAssignableChannelsInput = Schema.Struct({
  channels: Schema.NullOr(Schema.Array(Schema.String)),
  targetMongoUserId: Schema.optional(MongoUserId),
});

/**
 * `status: null` clears the override and returns the user to their schedule.
 * `targetMongoUserId` requires MANAGE_TEAM_USERS and defaults to the caller.
 */
export const SetAvailabilityOverrideInput = Schema.Struct({
  status: Schema.NullOr(AvailabilityOverrideStatus),
  targetMongoUserId: Schema.optional(MongoUserId),
});

export const SupportUserScheduleGroup = HttpApiGroup.make("supportUserSchedule")
  .add(
    endpoint.merchant
      .post("getWorkSchedule", "/support-user-schedule/get")
      .setPayload(GetWorkScheduleInput)
      .addSuccess(GetWorkScheduleResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("updateWorkSchedule", "/support-user-schedule/update")
      .setPayload(UpdateWorkScheduleInput)
      .addSuccess(GetWorkScheduleResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getMyShiftStatus", "/support-user-schedule/status")
      .addSuccess(ShiftStatusView)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setAvailabilityOverride", "/support-user-schedule/override")
      .setPayload(SetAvailabilityOverrideInput)
      .addSuccess(ShiftStatusView)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getShiftStatuses", "/support-user-schedule/statuses")
      .setPayload(GetShiftStatusesInput)
      .addSuccess(GetShiftStatusesResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    // NotFound rather than Forbidden when the caller cannot manage users, so a
    // non-manager cannot tell a real roster apart from an empty one.
    endpoint.merchant
      .post("getTeamWorkSchedules", "/support-user-schedule/team-schedules")
      .setPayload(GetTeamWorkSchedulesInput)
      .addSuccess(GetTeamWorkSchedulesResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("updateAssignableChannels", "/support-user-schedule/channels")
      .setPayload(UpdateAssignableChannelsInput)
      .addSuccess(WorkScheduleView)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
